/**
 * PropScope API Client
 * Axios-based HTTP client with environment-based configuration
 */

import axios from 'axios';

// API Base URL Configuration
// In production: empty string — all /api/... paths are relative to the same origin (nginx proxies)
// In development: set REACT_APP_API_URL=http://localhost:8000 in .env.local
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

console.log('API Base URL:', API_BASE_URL);

const API_TIMEOUT_MS = 30000;
const AUTH_TIMEOUT_MS = 15000;
/** Heavy reports (neighbourhood) — first uncached hit can exceed 15–20s. */
export const HEAVY_REPORT_TIMEOUT_MS = 60000;

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Token refresh
// ---------------------------------------------------------------------------
// Backend access token lives 30 min, refresh token lives 7 days. When a 401
// comes back we attempt a single refresh and retry the original request. A
// burst of concurrent 401s share one in-flight refresh so we don't spam the
// endpoint or rotate the refresh token multiple times in parallel.
let refreshPromise = null;

function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = localStorage.getItem('assetlens_refresh_token');
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'));
  }
  // Bare axios (not apiClient) so refresh can't recurse into the interceptor.
  refreshPromise = axios
    .post(`${API_BASE_URL}/api/auth/refresh`, { refresh_token: refreshToken }, {
      timeout: AUTH_TIMEOUT_MS,
    })
    .then((res) => {
      localStorage.setItem('assetlens_token', res.data.access_token);
      localStorage.setItem('assetlens_refresh_token', res.data.refresh_token);
      return res.data.access_token;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

export const AUTH_CLEARED_EVENT = 'propscope:auth-cleared';

function isAuthCredentialRequest(url = '') {
  return url.includes('/api/auth/login') || url.includes('/api/auth/register');
}

function clearAuth() {
  const hadSession =
    localStorage.getItem('assetlens_token') ||
    localStorage.getItem('assetlens_refresh_token');
  localStorage.removeItem('assetlens_token');
  localStorage.removeItem('assetlens_refresh_token');
  if (hadSession) {
    window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
  }
}

// ---------------------------------------------------------------------------
// authFetch — raw fetch with the same refresh behaviour
// ---------------------------------------------------------------------------
// Use this in place of window.fetch for any authenticated call that isn't
// going through apiClient. Adds the Authorization header from localStorage,
// retries once after a refresh on 401, and clears tokens if refresh fails.
export async function authFetch(input, init = {}) {
  const withTimeout = (ms) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return { signal: controller.signal, clear: () => clearTimeout(timer) };
  };

  const buildHeaders = (token) => {
    const h = new Headers(init.headers || {});
    if (token) h.set('Authorization', `Bearer ${token}`);
    return h;
  };

  // Default to API timeout (30s), not the short auth refresh timeout (15s).
  // Callers can pass timeoutMs for heavy endpoints (e.g. neighbourhood).
  const { timeoutMs, ...fetchInit } = init;
  const requestTimeoutMs = timeoutMs ?? API_TIMEOUT_MS;

  const doFetch = async (token) => {
    const { signal, clear } = withTimeout(requestTimeoutMs);
    try {
      return await fetch(input, {
        ...fetchInit,
        signal: fetchInit.signal || signal,
        headers: buildHeaders(token),
      });
    } finally {
      clear();
    }
  };

  let res = await doFetch(localStorage.getItem('assetlens_token'));

  if (res.status === 401 && localStorage.getItem('assetlens_refresh_token')) {
    try {
      const newToken = await refreshAccessToken();
      res = await doFetch(newToken);
    } catch (_) {
      clearAuth();
    }
  }

  return res;
}

// Request interceptor - Add auth tokens if needed
apiClient.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const token = localStorage.getItem('assetlens_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    // Try a token refresh + single retry before treating a 401 as fatal.
    // We skip retry if the failing request *was* the refresh endpoint, or
    // if we've already retried this request once.
    const originalRequest = error.config || {};
    const requestUrl = originalRequest.url || '';
    const isRefreshCall = requestUrl.includes('/api/auth/refresh');
    const isCredentialCall = isAuthCredentialRequest(requestUrl);
    if (
      error.response?.status === 401 &&
      !originalRequest._retried &&
      !isRefreshCall &&
      !isCredentialCall &&
      localStorage.getItem('assetlens_refresh_token')
    ) {
      originalRequest._retried = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshErr) {
        clearAuth();
        // Fall through to the standard 401 handler below.
      }
    }

    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.detail || error.response.data?.error || 'An error occurred';

      switch (status) {
        case 401:
          // Wrong password on login/register is not a session expiry — leave
          // storage alone and let the form show its error.
          if (!isCredentialCall) {
            console.error('Unauthorized access - clearing token');
            clearAuth();
          }
          break;

        case 403:
          // Forbidden
          console.error('Access forbidden:', message);
          break;

        case 404:
          // Not found
          console.error('Resource not found:', error.config.url);
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          console.error('Server error:', message);
          break;

        default:
          console.error(`API Error (${status}):`, message);
      }

      // Return formatted error
      return Promise.reject({
        status,
        message,
        data: error.response.data
      });
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server:', error.message);
      return Promise.reject({
        status: 0,
        message: 'No response from server. Please check your connection.',
        data: null
      });
    } else {
      // Error in request configuration
      console.error('Request configuration error:', error.message);
      return Promise.reject({
        status: -1,
        message: error.message,
        data: null
      });
    }
  }
);

// API Methods

/**
 * Health check endpoint
 */
export const checkHealth = async () => {
  const response = await apiClient.get('/health');
  return response.data;
};

/**
 * Get API status
 */
export const getApiStatus = async () => {
  const response = await apiClient.get('/health');
  return response.data;
};

/**
 * Properties API
 */
export const propertiesApi = {
  // Get all properties with filters
  getAll: async (filters = {}) => {
    const response = await apiClient.get('/api/properties', { params: filters });
    return response.data;
  },

  // Get single property by ID
  getById: async (id) => {
    const response = await apiClient.get(`/api/properties/${id}`);
    return response.data;
  },

  // Get property sales history
  getSalesHistory: async (id) => {
    const response = await apiClient.get(`/api/properties/${id}/sales-history`);
    return response.data;
  },

  // Mark property as reviewed
  markReviewed: async (id) => {
    const response = await apiClient.post(`/api/properties/${id}/review`);
    return response.data;
  }
};

/**
 * Areas API
 */
export const areasApi = {
  // Get area statistics
  getStats: async (postcode) => {
    const response = await apiClient.get(`/api/areas/${postcode}/stats`);
    return response.data;
  },

  // Get area trends
  getTrends: async (postcode) => {
    const response = await apiClient.get(`/api/areas/${postcode}/trends`);
    return response.data;
  }
};

/**
 * Dashboard API
 */
export const dashboardApi = {
  // Get dashboard summary stats. Pass { watchlistOnly: true } to scope
  // every counter, breakdown and the top-properties list to the caller's
  // saved watchlist.
  getStats: async ({ watchlistOnly = false } = {}) => {
    const params = watchlistOnly ? { watchlist_only: 'true' } : {};
    const response = await apiClient.get('/api/dashboard/stats', { params });
    return response.data;
  },
};

/**
 * Scrapers API
 */
export const scrapersApi = {
  getAll: async () => {
    const response = await apiClient.get('/api/scrapers');
    return response.data;
  },
  add: async (payload) => {
    const response = await apiClient.post('/api/scrapers', payload);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/api/scrapers/${id}`);
    return response.data;
  },
  update: async (id, payload) => {
    const response = await apiClient.patch(`/api/scrapers/${id}`, payload);
    return response.data;
  },
  toggle: async (id) => {
    const response = await apiClient.patch(`/api/scrapers/${id}/toggle`);
    return response.data;
  },
  run: async (id) => {
    const response = await apiClient.post(`/api/scrapers/${id}/run`);
    return response.data;
  },
  investigate: async (id) => {
    const response = await apiClient.post(`/api/scrapers/${id}/investigate`);
    return response.data;
  },
  investigatePending: async () => {
    const response = await apiClient.post('/api/scrapers/investigate-pending');
    return response.data;
  },
  hint: async (id, payload) => {
    const response = await apiClient.post(`/api/scrapers/${id}/hint`, payload);
    return response.data;
  },
  getLibrary: async () => {
    const response = await apiClient.get('/api/scrapers/library');
    return response.data;
  },
  getLogs: async (id, runId) => {
    const url = runId ? `/api/scrapers/${id}/logs?run_id=${runId}` : `/api/scrapers/${id}/logs`;
    const response = await apiClient.get(url);
    return response.data;
  },
};

// Export configured axios instance for custom requests
export default apiClient;
